import * as React from 'react';
import { trackException } from '../diagnostics/applicationInsights';

interface AppErrorBoundaryState {
  error?: Error;
}

export default class AppErrorBoundary extends React.Component<{ children?: React.ReactNode }, AppErrorBoundaryState> {
  public state: AppErrorBoundaryState = {};

  public static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  public componentDidMount() {
    window.addEventListener('error', this.onWindowError);
    window.addEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener('error', this.onWindowError);
    window.removeEventListener('unhandledrejection', this.onUnhandledRejection);
  }

  public componentDidCatch(error: Error) {
    trackException(error, { source: 'react-boundary' });
    if (typeof env === 'undefined' || env !== 'live') {
      console.error(error);
    }
  }

  public render() {
    if (this.state.error) {
      return (
        <div className="app-error-boundary">
          <div className="app-error-boundary__panel" role="alert">
            <h1 className="app-error-boundary__heading">Something went wrong</h1>
            <p className="app-error-boundary__message">The page hit an unexpected error. Please refresh and try again.</p>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Refresh page
            </button>
            {typeof env !== 'undefined' && env !== 'live' && (
              <pre className="app-error-boundary__details">{this.state.error.message}</pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  private onWindowError = (event: ErrorEvent) => {
    trackException(event.error || event.message, { source: 'window.error' });
  };

  private onUnhandledRejection = (event: PromiseRejectionEvent) => {
    trackException(event.reason, { source: 'unhandledrejection' });
  };
}
